/** Byte size of a canonical WAV header (RIFF + fmt + data chunk descriptors). */
const WAV_HEADER_SIZE = 44;

const BITS_PER_SAMPLE = 16;
const PCM_FORMAT_TAG = 1;
const MONO = 1;

const writeAscii = (view: DataView, offset: number, value: string): void => {
  for (let i = 0; i < value.length; i += 1) {
    view.setUint8(offset + i, value.charCodeAt(i));
  }
};

/**
 * Wrap raw little-endian 16-bit mono PCM in a WAV container.
 *
 * The realtime path streams headerless PCM, but the batch fallback uploads a
 * file and needs a format the server can identify — WAV adds 44 bytes and no
 * re-encoding, so a dropped session can be salvaged without touching the audio.
 */
export const pcmToWavBlob = (
  chunks: ArrayBuffer[],
  sampleRate: number,
): Blob => {
  const dataLength = chunks.reduce((total, chunk) => total + chunk.byteLength, 0);
  const header = new ArrayBuffer(WAV_HEADER_SIZE);
  const view = new DataView(header);
  const byteRate = (sampleRate * MONO * BITS_PER_SAMPLE) / 8;
  const blockAlign = (MONO * BITS_PER_SAMPLE) / 8;

  writeAscii(view, 0, "RIFF");
  view.setUint32(4, WAV_HEADER_SIZE - 8 + dataLength, true);
  writeAscii(view, 8, "WAVE");

  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true); // fmt chunk length
  view.setUint16(20, PCM_FORMAT_TAG, true);
  view.setUint16(22, MONO, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, BITS_PER_SAMPLE, true);

  writeAscii(view, 36, "data");
  view.setUint32(40, dataLength, true);

  return new Blob([header, ...chunks], { type: "audio/wav" });
};
